// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { toast } from "../components/ui/sonner";
import { Trash2, RotateCcw, Clock, AlertTriangle, Loader2, Building2, Users } from "lucide-react";
import { fmtD } from "../lib/date";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const KINDS = {
  projects: { list: "/projects/recycle-bin", restore: (id) => `/projects/${id}/restore`, purge: (id) => `/projects/${id}/purge`, icon: Building2 },
  leads: { list: "/leads/recycle-bin", restore: (id) => `/leads/${id}/restore`, purge: (id) => `/leads/${id}/purge`, icon: Users },
};

export default function RecycleBin() {
  const { lang, user, api, refresh } = useApp();
  const zh = lang === "zh";
  const isAdmin = ["admin", "ceo"].includes(user.role);
  const [params, setParams] = useSearchParams();
  const kind = params.get("tab") === "leads" ? "leads" : "projects";
  const canLeads = ["admin", "ceo", "sales_vp", "eng_manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(KINDS[kind].list); setRows(data || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  }, [api, kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const kinds = canLeads ? ["projects", "leads"] : ["projects"];
    Promise.all(kinds.map((k) => api.get(KINDS[k].list).then(({ data }) => [k, (data || []).length]).catch(() => [k, 0])))
      .then((pairs) => setCounts(Object.fromEntries(pairs)));
  }, [api, canLeads, rows.length]);

  const title = (p) => (kind === "leads" ? (p.clientName || p.name || p.id) : p.address);
  const restore = async (p) => {
    setBusy(p.id);
    try {
      await api.post(KINDS[kind].restore(p.id));
      toast.success(kind === "leads" ? (zh ? "客户已恢复" : "Lead restored") : (zh ? "项目已恢复" : "Project restored"));
      await load(); if (kind === "projects") await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const purge = async (p) => {
    const linked = kind !== "leads" && p.linked ? Object.entries({ urges: zh ? "催办" : "urges", changeOrders: zh ? "变更订单" : "change orders", subcontracts: zh ? "分包合同" : "subcontracts", paymentRequests: zh ? "付款申请" : "payment requests", files: zh ? "文件" : "files", notes: zh ? "笔记" : "notes" }).filter(([k]) => p.linked[k] > 0).map(([k, l]) => `${l} ${p.linked[k]}`).join("、") : "";
    const warn = linked ? (zh ? `\n将一并永久删除关联记录：${linked}。` : `\nLinked records will be deleted too: ${linked}.`) : "";
    if (!window.confirm(zh ? `彻底删除「${title(p)}」？此操作不可恢复。${warn}` : `Permanently delete "${title(p)}"? This cannot be undone.${warn}`)) return;
    setBusy(p.id);
    try { await api.delete(KINDS[kind].purge(p.id)); toast.success(zh ? "已彻底删除" : "Permanently deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="recycle-bin-page" data-tab={kind}>
      <div className="flex items-center gap-2 flex-wrap">
        <Trash2 className="h-6 w-6 text-red-600" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "回收站" : "Recycle Bin"}</h1>
        <span className="text-sm text-slate-400">· {rows.length}</span>
        {canLeads && (
          <div className="ml-auto inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-medium" data-testid="recycle-tabs">
            {[["projects", zh ? "工程项目" : "Projects", Building2], ["leads", zh ? "客户" : "Leads", Users]].map(([k, l, Icon]) => (
              <button key={k} data-testid={`recycle-tab-${k}`} onClick={() => setParams(k === "projects" ? {} : { tab: k }, { replace: true })}
                className={"max-md:min-h-11 max-md:min-w-11 " + (`inline-flex items-center gap-1 px-3 py-1.5 rounded-md ${kind === k ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`)}><Icon className="h-3.5 w-3.5" />{l}{counts[k] > 0 && <span data-testid={`recycle-tab-count-${k}`} className={`ml-0.5 text-[10px] px-1.5 rounded-full ${kind === k ? "bg-white/20" : "bg-rose-100 text-rose-600"}`}>{counts[k]}</span>}</button>
            ))}
          </div>
        )}
      </div>
      <p className="text-sm text-slate-500 flex items-center gap-1.5">
        <Clock className="h-4 w-4 text-slate-400" />
        {kind === "leads"
          ? (zh ? (["admin", "ceo", "sales_vp"].includes(user.role) ? "删除的客户在此保留 15 天，期间可随时恢复；15 天后系统自动彻底删除。" : "这里显示由你删除的客户，保留 15 天可恢复；全部客户回收站请联系 IT 管理员 / CEO。") : "Deleted leads are kept here for 15 days and can be restored; auto-purged after 15 days.")
          : (zh ? "删除的项目在此保留 15 天，期间可随时恢复；15 天后系统自动彻底删除。" : "Deleted projects are kept here for 15 days and can be restored anytime; auto-purged after 15 days.")}
      </p>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="recycle-empty">{zh ? "回收站为空" : "Recycle bin is empty"}</div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => {
            const urgent = p.daysLeft <= 3;
            return (
              <div key={p.id} data-testid={`recycle-row-${p.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                  <p className="text-sm font-semibold text-slate-800 truncate">{title(p)}</p>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                    {kind === "leads"
                      ? `${p.phone || "-"} · ${p.address || "-"} · ${zh ? "负责销售" : "Sales"} ${p.assignedSales || "-"}`
                      : `${p.clientName || "-"} · ${p.teamName || p.team || "-"} · ${zh ? "合同" : "Contract"} ${money(p.contractAmount)}`}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{zh ? "删除人" : "Deleted by"}: {p.deletedBy || "-"} · {fmtD(p.deletedAt)}</p>
                  {kind !== "leads" && p.linked && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`recycle-linked-${p.id}`} data-total={Object.values(p.linked).reduce((a, b) => a + (b || 0), 0)}>
                      {[["urges", zh ? "催办" : "Urges"], ["changeOrders", zh ? "变更订单" : "Change orders"], ["subcontracts", zh ? "分包合同" : "Subcontracts"], ["paymentRequests", zh ? "付款申请" : "Pay requests"], ["files", zh ? "文件" : "Files"], ["notes", zh ? "笔记" : "Notes"]].map(([k, l]) => p.linked[k] > 0 && (
                        <span key={k} data-testid={`recycle-linked-${p.id}-${k}`} className={`text-[10px] px-1.5 py-0.5 rounded-full ${["urges", "changeOrders", "subcontracts"].includes(k) ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-50 text-slate-500 border border-slate-200"}`}>{l} {p.linked[k]}</span>
                      ))}
                      {Object.values(p.linked).every((n) => !n) && <span className="text-[10px] text-slate-400">{zh ? "无关联记录" : "No linked records"}</span>}
                    </div>
                  )}
                </div>
                <span data-testid={`recycle-daysleft-${p.id}`} className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${urgent ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                  {urgent && <AlertTriangle className="h-3 w-3" />}{zh ? `剩 ${p.daysLeft} 天` : `${p.daysLeft}d left`}
                </span>
                <Button data-testid={`recycle-restore-${p.id}`} size="sm" variant="outline" disabled={busy === p.id} className="text-xs h-8 text-green-700 border-green-200 hover:bg-green-50" onClick={() => restore(p)}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />{zh ? "恢复" : "Restore"}
                </Button>
                {isAdmin && (
                  <Button data-testid={`recycle-purge-${p.id}`} size="sm" variant="ghost" disabled={busy === p.id} className="text-xs h-8 text-red-600 hover:bg-red-50" onClick={() => purge(p)}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" />{zh ? "彻底删除" : "Delete"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
